import React, { Component } from 'react'
import { observer, inject, propTypes as mobxPropTypes } from 'mobx-react'
import { extendObservable, toJS } from 'mobx'
import Button from '@components/Button'
import Checkbox from '@components/Checkbox'
import FormLoader from '@components/FormLoader'
import { deliverableApi, invoiceApi } from '@atheneum/api'
import PropTypes from 'prop-types'
import FormError from '@components/FormError'
import InfoBox from '@components/InfoBox'
import { getFeeItem , formatDate, formatNumber} from '@atheneum/api/dist/lib'
import flatten from 'lodash/flatten'
import classNames from 'classnames'
import pick from 'lodash/pick'

class InvoiceFeesForm extends Component {
  static propTypes = {
    modalStore: PropTypes.object,
    staticData: PropTypes.object,
    title: PropTypes.string,
    invoice: PropTypes.object,
    contractStore: PropTypes.object,
    growlStore: PropTypes.object,
    invoiceStore: PropTypes.object,
    deliverableStore: PropTypes.object
  }

  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: true,
    wideContent: true
  }

  constructor (props) {
    super(props)
    extendObservable(this, {
      error: null,
      loadingDeliverables: false,
      isSubmittingForm: false,
      fees: [],
      selectedFees: []
    })
  }

  componentDidMount = () => {
    this.loadFees()
  }

  loadFees = async () => {
    try {
      const { invoice, staticData } = this.props
      const { INVOICE_TYPES } = staticData.CONST

      this.loadingDeliverables = true
      this.error = null
      let deliverables = []

      if (invoice.invoiceTypeId === INVOICE_TYPES.PAYG) {
        deliverables = await deliverableApi.getProjectDeliverables(invoice.projectId, {
          paygOnly: true
        })
      } else if (invoice.invoiceTypeId === INVOICE_TYPES.NON_PAYG) {
        deliverables = await deliverableApi.getContractDeliverables(invoice.contractId)
      }

      const fees = extractFeesFromDeliverables(deliverables)
      const nonInvoicedAndSelectedFees = fees.filter(
        fee => !fee.invoiceId || fee.invoiceId === invoice.id
      )
      this.fees.replace(nonInvoicedAndSelectedFees)

      const selectedFees = fees.filter(fee => fee.invoiceId === invoice.id)
      this.selectedFees.replace(selectedFees)

      this.loadingDeliverables = false
    } catch (error) {
      this.error = error
      this.loadingDeliverables = false
    }
  }

  submitForm = async event => {
    event.preventDefault()

    const {
      invoice,
      invoiceStore,
      modalStore,
      growlStore,
      deliverableStore,
      staticData
    } = this.props

    const { INVOICE_TYPES } = staticData.CONST
    const isPayg = invoice.invoiceTypeId === INVOICE_TYPES.PAYG
    const isNonPayg = invoice.invoiceTypeId === INVOICE_TYPES.NON_PAYG
    const fees = this.selectedFees.map(fee => ({ id: fee.id }))

    const relevantInvoiceData = pick(invoice, [
      'id',
      'invoiceTypeId',
      'invoicingEntityId',
      'atheneumOfficeId',
      'vat',
      'caseCode',
      'comment',
      'currencyId',
      'internalRemarks',
      'startDate',
      'endDate',
      'isWriteOff',
      'hideExpertName'
    ])

    const invoiceData = {
      ...relevantInvoiceData,
      projectId: isPayg ? invoice.projectId : undefined,
      contractId: isNonPayg ? invoice.contractId : undefined,
      fees,
      invoiceItems:
        invoice.invoiceItems && invoice.invoiceItems.length
          ? toJS(invoice.invoiceItems)
          : undefined
    }

    this.loading = true
    this.error = null

    const isInvoiceCreation = !invoice.id

    try {
      if (isInvoiceCreation) {
        const createdInvoice = await invoiceApi.createInvoice(invoiceData)

        if (isPayg) {
          invoiceStore.getListByProject(createdInvoice.projectId)
          deliverableStore.getByProject(createdInvoice.projectId)
        } else if (isNonPayg) {
          invoiceStore.getListByContract(createdInvoice.contractId)
          deliverableStore.getByContract(createdInvoice.contractId)
        }

        growlStore.showSuccess(`Created invoice ${createdInvoice.name}`)
      } else {
        await invoiceApi.updateInvoice(invoiceData)
        invoiceStore.get(invoice.id)
        growlStore.showSuccess('Updated invoice data.')
      }

      modalStore.close()
      this.loading = false
      this.error = null
    } catch (error) {
      this.error = error
      this.loading = false
      growlStore.showError(error)
    }
  }

  toggleFeeSelection = fee => {
    const feeIndex = this.selectedFees.findIndex(f => f.id === fee.id)
    const isUnselected = feeIndex === -1

    if (isUnselected) {
      this.selectedFees.push(fee)
    } else {
      this.selectedFees.splice(feeIndex, 1)
    }
  }

  onToggleAllFeesSelection = () => {
    const { staticData, invoice } = this.props
    const eligibleFees = filterInvoiceFeeCandidates(
      staticData,
      invoice,
      this.fees
    )
    const areAllSelected = this.selectedFees.length === eligibleFees.length

    if (areAllSelected) {
      this.selectedFees.clear()
    } else {
      this.selectedFees.replace(eligibleFees)
    }
  }

  render () {
    const { title, modalStore, staticData, contractStore, invoice } = this.props
    const currencyNames = staticData.names.currencies
    const currencyName = currencyNames[invoice.currencyId] || 'N/A'

    return (
      <div className='expert-form invoice-form'>
        <FormLoader
          loading={this.isSubmittingForm || this.loadingDeliverables}
        />
        <h1 className='expert-form__title'>{title}</h1>
        <FormError error={this.error} />

        {invoice.isPayg && (
          <InfoBox>
            Only fees with the same currency as invoice are shown (
            {currencyName})
          </InfoBox>
        )}

        <form onSubmit={this.submitForm}>
          <FeeTable
            contract={contractStore.contract}
            invoice={invoice}
            fees={this.fees}
            selectedFees={this.selectedFees}
            staticData={staticData}
            onToggleFeeSelection={this.toggleFeeSelection}
            onToggleAllFeesSelection={this.onToggleAllFeesSelection}
          />

          <div className='expert-form__section p-b-0'>
            <Button
              primary
              submit
              disabled={this.isSubmittingForm}
              data-cy='save-add-fees-btn'
            >
              Save
            </Button>
            <Button onClick={modalStore.close}>Cancel</Button>
          </div>
        </form>
      </div>
    )
  }
}

const FeeTable = observer(
  ({
    invoice,
    contract,
    fees,
    selectedFees,
    staticData,
    onToggleFeeSelection,
    onToggleAllFeesSelection
  }) => {
    const invoiceName = invoice ? invoice.name : null

    const invoiceFeeCandidates = filterInvoiceFeeCandidates(
      staticData,
      invoice,
      fees
    )

    if (!invoiceFeeCandidates.length) {
      return <p>No fees available</p>
    }

    const tableBody = invoiceFeeCandidates.map(fee => {
      const isChecked = selectedFees.find(f => f.id === fee.id)
      const feeItem = getFeeItem(fee)

      if (!feeItem || !feeItem.currencyId) return null

      const feeItemType = staticData.names.feeTypes[feeItem.feeTypeId]
      const feeItemCurrency = staticData.names.currencies[feeItem.currencyId]
      const feeItemAmount = feeItem.value || 0
      const feeItemConvertedAmount = feeItem.converted || 0
      const classes = classNames({
        'clickable-row': true,
        'selected-tr': isChecked
      })

      let contractReference
      if (fee.contract) {
        contractReference = fee.contract.contractReference
      } else if (contract) {
        contractReference = contract.contractReference
      }

      return (
        <tr
          key={fee.id}
          className={classes}
          onClick={e => onToggleFeeSelection(fee)}
        >
          <td className='check-td'>
            <Checkbox value={isChecked} />
          </td>
          <td className='on-select-bold'>
            {fee.parentName} - {feeItemType} <br />
            {fee.expert ? `(${fee.expert.title} ${fee.expert.fullName})` : ''}
          </td>
          <td>{fee.deliveryDate ? formatDate(fee.deliveryDate) : '-'}</td>
          <td>{contractReference || '-'}</td>
          <td className='right-text'>
            <b>{formatNumber(feeItemAmount)}</b> {feeItemCurrency}
          </td>
          <td className='right-text'>
            <b>{formatNumber(feeItemConvertedAmount)}</b> EUR
          </td>
        </tr>
      )
    })

    const areAllFeesChecked =
      selectedFees.length === invoiceFeeCandidates.length

    return (
      <div className='expert-form__section p-b-0'>
        <h2 className='expert-form__subtitle'>
          Select fees to be allocated to the invoice {invoiceName}
        </h2>
        <RemainingAmount
          selectedFees={selectedFees}
          staticData={staticData}
          invoice={invoice}
        />

        <table className='table-wrapper--nested invoice-table'>
          <thead>
            <tr>
              <th className='p0'>
                <Checkbox
                  onChange={onToggleAllFeesSelection}
                  value={areAllFeesChecked}
                />
              </th>
              <th>Fee type</th>
              <th>Delivery date</th>
              <th>Contract</th>
              <th className='right-text'>Amount</th>
              <th className='right-text'>Amount (EUR)</th>
            </tr>
          </thead>

          <tbody>{tableBody}</tbody>
        </table>
      </div>
    )
  }
)

function RemainingAmount ({ selectedFees, staticData, invoice }) {
  const { INVOICE_TYPES } = staticData.CONST
  if (invoice.invoiceTypeId === INVOICE_TYPES.PAYG) return null

  const currencyName = invoice.currencyId
    ? staticData.names.currencies[invoice.currencyId]
    : ''

  const conversionRate = invoice.invoiceItems[0]
    ? invoice.invoiceItems[0].conversionRate
    : 0

  const spentAmountEur = calculateSpentAmount(selectedFees, 'converted')

  const availableAmountEur = invoice.netAmountEur - spentAmountEur
  const availableAmount = availableAmountEur / conversionRate

  const amountClass = availableAmount >= 0 ? 'text-success' : 'text-error'

  return (
    <h2>
      Remaining available amount:{' '}
      <span className={amountClass}>
        {formatNumber(availableAmount)} {currencyName} (
        {formatNumber(availableAmountEur)} EUR)
      </span>
    </h2>
  )
}

function calculateSpentAmount (fees, prop = 'value') {
  return fees.reduce((total, fee) => {
    const feeItem = getFeeItem(fee)
    return total + feeItem[prop]
  }, 0)
}

RemainingAmount.propTypes = {
  selectedFees: PropTypes.oneOfType([
    PropTypes.array,
    mobxPropTypes.observableArray
  ]).isRequired,
  staticData: PropTypes.object.isRequired,
  invoice: PropTypes.object.isRequired
}

function extractFeesFromDeliverables (deliverables) {
  const feeArrays = deliverables.map(deliverable =>
    deliverable.fees.map(fee => ({
      ...fee,
      parentName: deliverable.name
    }))
  )

  const fees = flatten(feeArrays)

  return fees
}

function filterInvoiceFeeCandidates (staticData, invoice, fees) {
  const { INVOICE_TYPES } = staticData.CONST
  const attachableFees = fees.filter(fee => isFeeAttachable(invoice, fee))

  if (invoice.invoiceTypeId === INVOICE_TYPES.PAYG) {
    return attachableFees.filter(fee =>
      doFeeAndInvoiceShareSameCurrency(invoice, fee)
    )
  } else {
    return attachableFees
  }
}

function isFeeAttachable (invoice, fee) {
  if (!fee.invoiceId) return true
  if (invoice.id && fee.invoiceId !== invoice.id) return false
  return true
}

function doFeeAndInvoiceShareSameCurrency (invoice, fee) {
  const feeItem = getFeeItem(fee)
  if (!feeItem) return false

  return invoice.currencyId === feeItem.currencyId
}

export default inject(
  'invoiceStore',
  'staticData',
  'modalStore',
  'contractStore',
  'deliverableStore',
  'growlStore'
)(observer(InvoiceFeesForm))
